import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useGoogleLogin } from '@react-oauth/google';
import { useNavigate } from 'react-router-dom';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, Mail, Check, X } from 'lucide-react';
import { COUNTRIES } from '../constants/countries';

export default function LabAuthDialog({ open, onClose, onSuccess, pendingMessage, initialMode = 'login', requiredRoles = null, initialRole = null, onSwitchToEnquiry = null }) {
  const navigate = useNavigate();
  const [loginEmail, setLoginEmail]       = useState('');
  const [loginOtp, setLoginOtp]           = useState('');
  const [loginStep, setLoginStep]         = useState('email'); // 'email' | 'otp'
  const [loginLoading, setLoginLoading]   = useState(false);
  const [loginMode, setLoginMode]         = useState(initialMode);

  const [registerName, setRegisterName]   = useState('');
  const [registerPhone, setRegisterPhone] = useState('');
  const [registerOrg, setRegisterOrg]     = useState('');
  const [registerCity, setRegisterCity]   = useState('');
  const [registerCountry, setRegisterCountry] = useState('');
  const [registerRole, setRegisterRole]   = useState('researcher');

  useEffect(() => {
    if (open) {
      setLoginMode(initialMode);
      setLoginStep('email');
      if (initialRole) setRegisterRole(initialRole);
    }
  }, [open, initialMode, initialRole]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  function storeSession(sessionToken, userData) {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('dist_token');
    sessionStorage.removeItem('dist_user');
    sessionStorage.setItem('lab_session_token', sessionToken);
    sessionStorage.setItem('biomall_lab_user', JSON.stringify({
      ...userData,
      user_type: userData?.user_type || 'researcher',
      role:      userData?.user_type || 'researcher',
      _authSystem: 'lab',
    }));
  }

  // ── Google Sign-In ─────────────────────────────────────────────────────────
  const handleGoogleSuccess = async (tokenResponse) => {
    setLoginLoading(true);
    try {
      const userInfoRes = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
        headers: { Authorization: `Bearer ${tokenResponse.access_token}` }
      });
      const userInfo = await userInfoRes.json();

      const res = await axios.post('/api/lab-list/auth/google', {
        credential:    tokenResponse.access_token,
        access_token:  tokenResponse.access_token,
        google_email:  userInfo.email,
        google_name:   userInfo.name,
        google_picture: userInfo.picture,
        google_sub:    userInfo.sub,
      });

      const { session_token, needs_profile, user } = res.data;

      if (needs_profile) {
        // Redirect to a dedicated full-page step-2 flow instead of an in-modal popup —
        // most users treated the popup as "extra work" and abandoned it.
        sessionStorage.setItem('g_complete_token', session_token);
        setLoginLoading(false);
        onClose();
        navigate('/complete-profile');
        return;
      }

      storeSession(session_token, user);
      sessionStorage.removeItem('pending_register_role');
      toast.success('Signed in with Google!');
      onSuccess(user);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Google sign-in failed');
    }
    setLoginLoading(false);
  };

  const googleLogin = useGoogleLogin({
    onSuccess: handleGoogleSuccess,
    onError: () => toast.error('Google sign-in was cancelled or failed'),
  });

  if (!open) return null;

  // ── OTP Flow ───────────────────────────────────────────────────────────────
  const handleSendLoginOtp = async () => {
    setLoginLoading(true);
    try {
      if (loginMode === 'register') {
        if (!registerName || !registerPhone || !registerOrg || !registerCountry || !registerCity) {
          toast.error('Please fill all required fields');
          setLoginLoading(false);
          return;
        }
        sessionStorage.setItem('pending_register_role', registerRole);
        await axios.post(`/api/lab-list/register`, {
          email:        loginEmail,
          name:         registerName,
          phone:        registerPhone,
          organization: registerOrg,
          city:         registerCity,
          country:      registerCountry,
          user_type:    registerRole,
        });
      }
      await axios.post(`/api/lab-list/login/send-otp`, { email: loginEmail });
      setLoginStep('otp');
      toast.success('Verification code sent!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send code');
    }
    setLoginLoading(false);
  };

  const handleVerifyOtp = async () => {
    setLoginLoading(true);
    try {
      const res = await axios.post(`/api/lab-list/login/verify-otp`, {
        email: loginEmail,
        otp: loginOtp,
      });

      if (res.data.needs_profile) {
        // Same account exists (e.g. abandoned Google sign-up) but profile is incomplete —
        // route through the same one-time full-page completion step, don't grant full login.
        sessionStorage.setItem('g_complete_token', res.data.session_token);
        setLoginLoading(false);
        setLoginStep('email');
        setLoginOtp('');
        onClose();
        navigate('/complete-profile');
        return;
      }

      const savedRole = res.data.user?.user_type ||
                        res.data.user?.role ||
                        sessionStorage.getItem('pending_register_role') ||
                        'researcher';
      storeSession(res.data.session_token, { ...res.data.user, user_type: savedRole });
      sessionStorage.removeItem('pending_register_role');
      toast.success('Logged in!');
      setLoginStep('email');
      setLoginOtp('');
      onSuccess(res.data.user);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code');
    }
    setLoginLoading(false);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">
            {loginMode === 'register' ? 'Create Account' : 'Sign In'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {pendingMessage && (
          <p className="text-sm text-slate-500 mb-4">{pendingMessage}</p>
        )}

        {/* ── OTP Step ── */}
        {loginStep === 'otp' && (
          <div className="space-y-3">
            <div>
              <Label>Verification Code</Label>
              <Input value={loginOtp} onChange={e => setLoginOtp(e.target.value)} placeholder="Enter code from email" className="mt-1" data-testid="otp-input" />
            </div>
            <Button
              className="w-full bg-blue-600 hover:bg-blue-700"
              onClick={handleVerifyOtp}
              disabled={loginLoading || !loginOtp}
              data-testid="otp-verify-btn"
            >
              {loginLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
              Verify & Sign In
            </Button>
            <button className="text-xs text-slate-500 hover:text-blue-600 w-full text-center" onClick={() => setLoginStep('email')}>Back</button>
          </div>
        )}

        {/* ── Email Step ── */}
        {loginStep === 'email' && (
          <div className="space-y-3">
            {/* Google Sign-In — primary CTA */}
            <button
              onClick={() => { if (!loginLoading) googleLogin(); }}
              disabled={loginLoading}
              data-testid="google-signin-btn"
              className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border-2 border-gray-200 rounded-xl hover:border-blue-300 hover:bg-gray-50 transition-all font-semibold text-sm text-gray-700 disabled:opacity-60"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            {/* Divider */}
            <div className="flex items-center gap-2">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs text-gray-400">or use email</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            {/* Register fields */}
            {loginMode === 'register' && (
              <>
                <div className="mb-2">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">What describes you best? *</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { value: 'researcher',   label: 'Buyer',        sub: 'I source / purchase lab products' },
                      { value: 'distributor',  label: 'Distributor',  sub: 'I sell & supply lab products' },
                      { value: 'manufacturer', label: 'Manufacturer', sub: 'I manufacture lab products' },
                    ].map(r => {
                      const isAllowed = !requiredRoles || requiredRoles.includes(r.value);
                      return (
                        <button key={r.value} type="button" onClick={() => setRegisterRole(r.value)}
                          className={`p-2.5 border-2 rounded-xl text-left transition-all relative ${registerRole === r.value ? isAllowed ? 'border-blue-500 bg-blue-50' : 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-blue-300'} ${!isAllowed ? 'opacity-60' : ''}`}
                        >
                          <div className="text-xs font-bold text-gray-900">{r.label}</div>
                          <div className="text-xs text-gray-500">{r.sub}</div>
                        </button>
                      );
                    })}
                  </div>
                  {requiredRoles && !requiredRoles.includes(registerRole) && (
                    <div className="mt-2.5 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                      <p className="text-xs font-semibold text-amber-800 mb-1">Buyers can't post stock updates or offers.</p>
                      <p className="text-xs text-amber-700 mb-2">To post, select <strong>Distributor</strong> or <strong>Manufacturer</strong> above.</p>
                      {onSwitchToEnquiry && (
                        <button type="button" onClick={() => { onClose(); onSwitchToEnquiry(); }} className="text-xs font-semibold text-blue-600 hover:text-blue-800 underline underline-offset-2">
                          I'm a buyer — switch to Post Enquiry instead →
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div><Label>Full Name *</Label><Input value={registerName} onChange={e => setRegisterName(e.target.value)} placeholder="Your name" className="mt-1" /></div>
                <div><Label>Phone *</Label><Input value={registerPhone} onChange={e => setRegisterPhone(e.target.value)} placeholder="Phone number" className="mt-1" /></div>
                <div><Label>Organisation *</Label><Input value={registerOrg} onChange={e => setRegisterOrg(e.target.value)} placeholder="e.g. IIT Bombay, Cipla Ltd, Apollo Hospitals, Metropolis Labs" className="mt-1" /></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <Label>Country *</Label>
                    <Select value={registerCountry} onValueChange={setRegisterCountry}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Select country" /></SelectTrigger>
                      <SelectContent className="z-[10000]">{COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>City *</Label><Input value={registerCity} onChange={e => setRegisterCity(e.target.value)} placeholder="e.g. Mumbai" className="mt-1" /></div>
                </div>
              </>
            )}

            <div>
              <Label>Email</Label>
              <Input type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} placeholder="your@email.com" className="mt-1" data-testid="email-input" />
            </div>
            <Button
              className="w-full bg-blue-600 hover:bg-blue-700"
              onClick={handleSendLoginOtp}
              disabled={loginLoading || !loginEmail || (loginMode === 'register' && requiredRoles && !requiredRoles.includes(registerRole))}
              data-testid="send-otp-btn"
            >
              {loginLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
              {loginMode === 'register' ? 'Create account & send code' : 'Send Verification Code'}
            </Button>
            <p className="text-xs text-center text-slate-500">
              {loginMode === 'login'
                ? <>New user? <button className="text-blue-600 hover:underline" onClick={() => setLoginMode('register')}>Create account</button></>
                : <>Already registered? <button className="text-blue-600 hover:underline" onClick={() => setLoginMode('login')}>Sign in</button></>
              }
            </p>
            {loginMode === 'register' && (
              <p className="text-xs text-center text-slate-400 mt-1">
                By creating an account, you agree to our{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">Terms & Conditions</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">Privacy Policy</a>.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
